import { AnalyticsContext } from '@carvana/eventable-clickstream';
import { datadogRum } from '@datadog/browser-rum';
import { useCallback, useContext, useEffect, useRef } from 'react';
import { PageEventContext } from '|/context';
import PurchaseContext from '|/context/purchase-context';
import UserInitializationContext from '|/context/user-initialization-context';
import { isNativeWebview } from '|/helpers/is-native-app';
import { MobileWebviewEvents } from '|/helpers/mobile-webview-events';
import sendNativeEvent from '|/helpers/send-native-event';

// Use fetch instead of sendBeacon in development so payloads are visible in Network tab
const USE_FETCH_FOR_ANALYTICS = process.env.NODE_ENV === 'development';

export default function useAnalyticsContext() {
  const context = useContext(AnalyticsContext);

  if (!context) {
    throw new Error('useAnalyticsContext must be used within an AnalyticsProvider');
  }

  const { trackEvent: baseTrackEvent, getPayload } = context;
  const { vehicleId } = useContext(PurchaseContext);
  const pageEventContext = useContext(PageEventContext);
  const { accessToken } = useContext(UserInitializationContext);

  const vehicleIdRef = useRef(vehicleId);
  useEffect(() => {
    vehicleIdRef.current = vehicleId;
  }, [vehicleId]);

  const pageEventContextRef = useRef(pageEventContext);
  useEffect(() => {
    pageEventContextRef.current = pageEventContext;
  }, [pageEventContext]);

  const trackEvent = useCallback(
    async (eventName: string, eventProperties?: Record<string, unknown>) => {
      const datadogContext = datadogRum.getInternalContext();

      const callerMetadata = (eventProperties?.metadata as object | undefined) ?? null;
      const pageContext = pageEventContextRef.current;
      const mergedMetadata =
        pageContext || callerMetadata
          ? { ...(pageContext ?? {}), ...(callerMetadata ?? {}) }
          : undefined;

      const enrichedProperties = {
        ...eventProperties,
        ...(mergedMetadata && { metadata: mergedMetadata }),
        customerId: accessToken?.user?.user_id ?? null,
        vehicleId: vehicleIdRef.current,
        datadogSessionId: datadogContext?.session_id,
        datadogViewId: datadogContext?.view?.id
      };

      const { datadogSessionId, datadogViewId, ...actionContext } = enrichedProperties;
      datadogRum.addAction(eventName, actionContext);

      // let native handle all events
      if (isNativeWebview()) {
        const payload = await getPayload({
          eventName,
          customAttributes: enrichedProperties
        });
        sendNativeEvent({
          event: MobileWebviewEvents.WEBVIEW_EVENT,
          payload: payload,
          source: 'purchase'
        });
      }
      baseTrackEvent(eventName, enrichedProperties, {}, { useFetch: USE_FETCH_FOR_ANALYTICS });
    },
    [baseTrackEvent, getPayload, accessToken]
  );

  const sendCompleteEvent = useCallback(
    (metadata?: object) => {
      const attributesToSend = {
        url: window.location.pathname,
        userId: accessToken?.user.user_id,
        successEvent: true,
        metadata: metadata ?? {},
        devicePlatform: 'web'
      };
      trackEvent('pp_complete', attributesToSend);
    },
    [trackEvent, accessToken]
  );

  return { trackEvent, sendCompleteEvent };
}
